<script setup>
import { ref, watch, onMounted, nextTick } from 'vue'

const props = defineProps({
	modelValue: { type: String, default: '' },
	length: { type: Number, default: 6 },
	autofocus: { type: Boolean, default: true },
	hasError: { type: Boolean, default: false },
	disabled: { type: Boolean, default: false },
	// Dims the cells while the code is being checked against the backend.
	verifying: { type: Boolean, default: false },
	// Flashes the cells green once the code is accepted.
	success: { type: Boolean, default: false },
})

const emit = defineEmits(['update:modelValue', 'complete'])

const digits = ref(Array.from({ length: props.length }, () => ''))
const inputs = ref([])
const shaking = ref(false)

watch(
	() => props.modelValue,
	(val) => {
		const chars = (val || '').split('')
		for (let i = 0; i < props.length; i++) digits.value[i] = chars[i] || ''
	},
)

function emitValue() {
	const value = digits.value.join('')
	emit('update:modelValue', value)
	if (value.length === props.length) emit('complete', value)
}

function focusCell(i) {
	nextTick(() => inputs.value[i]?.focus())
}

function onInput(event, i) {
	const raw = (event.target.value || '').replace(/\D/g, '')

	if (!raw) {
		digits.value[i] = ''
		emitValue()
		return
	}

	// Multiple chars (autofill / paste into one cell): distribute across cells.
	if (raw.length > 1) {
		const chars = raw.split('')
		for (let j = 0; i + j < props.length && j < chars.length; j++) {
			digits.value[i + j] = chars[j]
		}
		emitValue()
		focusCell(Math.min(i + chars.length, props.length - 1))
		return
	}

	digits.value[i] = raw
	emitValue()
	if (i < props.length - 1) focusCell(i + 1)
}

function onKeydown(event, i) {
	if (event.key === 'Backspace') {
		event.preventDefault()
		if (digits.value[i]) {
			digits.value[i] = ''
		} else if (i > 0) {
			digits.value[i - 1] = ''
			focusCell(i - 1)
		}
		emitValue()
	} else if (event.key === 'ArrowLeft' && i > 0) {
		focusCell(i - 1)
	} else if (event.key === 'ArrowRight' && i < props.length - 1) {
		focusCell(i + 1)
	}
}

function onPaste(event) {
	event.preventDefault()
	const text = (event.clipboardData?.getData('text') || '')
		.replace(/\D/g, '')
		.slice(0, props.length)
	for (let i = 0; i < props.length; i++) digits.value[i] = text[i] || ''
	emitValue()
	focusCell(Math.min(text.length, props.length - 1))
}

function clear() {
	for (let i = 0; i < props.length; i++) digits.value[i] = ''
	emit('update:modelValue', '')
	focusCell(0)
}

function shake() {
	shaking.value = false
	// Restart the animation even if a shake is already in flight.
	requestAnimationFrame(() => {
		shaking.value = true
		setTimeout(() => {
			shaking.value = false
		}, 500)
	})
}

onMounted(() => {
	if (props.autofocus) focusCell(0)
})

defineExpose({ focus: () => focusCell(0), clear, shake })
</script>

<template>
	<div
		class="otp-group"
		:class="{ 'otp-group--shake': shaking, 'otp-group--verifying': verifying }"
		@paste="onPaste"
	>
		<input
			v-for="(digit, i) in digits"
			:key="i"
			ref="inputs"
			class="otp-cell"
			:class="{
				'otp-cell--filled': !!digit,
				'otp-cell--error': hasError,
				'otp-cell--success': success,
			}"
			type="text"
			inputmode="numeric"
			:autocomplete="i === 0 ? 'one-time-code' : 'off'"
			maxlength="1"
			:disabled="disabled || verifying || success"
			:value="digit"
			@input="onInput($event, i)"
			@keydown="onKeydown($event, i)"
			@focus="$event.target.select()"
		/>
	</div>
</template>

<style scoped>
.otp-group {
	display: flex;
	gap: 8px;
	justify-content: center;
}
.otp-group--verifying {
	opacity: 0.55;
	pointer-events: none;
}
.otp-group--shake {
	animation: otp-shake 0.4s ease;
}
@keyframes otp-shake {
	0%,
	100% {
		transform: translateX(0);
	}
	20% {
		transform: translateX(-6px);
	}
	40% {
		transform: translateX(6px);
	}
	60% {
		transform: translateX(-4px);
	}
	80% {
		transform: translateX(4px);
	}
}
.otp-cell {
	flex: 1 1 0;
	min-width: 0;
	height: 54px;
	padding: 0;
	text-align: center;
	font-size: 22px;
	font-weight: 600;
	color: var(--castr-text);
	background-color: var(--castr-field);
	border: 1.5px solid var(--castr-border);
	border-radius: 12px;
	caret-color: var(--castr-blue);
	transition:
		border-color 0.15s ease,
		background-color 0.15s ease,
		box-shadow 0.15s ease,
		opacity 0.15s ease;
}
.otp-cell--filled {
	border-color: var(--castr-border-strong);
}
.otp-cell:focus {
	outline: none;
	border-color: var(--castr-blue);
	box-shadow: 0 0 0 3px var(--castr-focus-ring);
}
.otp-cell--error {
	border-color: var(--castr-danger);
}
.otp-cell--success {
	border-color: var(--castr-success);
	box-shadow: 0 0 0 3px rgba(52, 211, 153, 0.18);
}
</style>
